import { Stack, Typography } from '@mui/material';
import { ReactNode } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { ButtonVariant } from '@/components/form/baseInputs/Button/types';
import { Card } from '@/components/layout/Card/Card';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { getTerminalStatusText } from '@/modules/household/chat/components/messages/interactive/interactiveMessage.utils';
import {
  HouseholdChatMessage,
  useHouseholdChatContext,
} from '@/modules/household/chat/contexts/householdChat.context';
import { COLORS } from '@/styles/tokens/colors';
import {
  MessageResponseAction,
  MessageStatus,
  MessageType,
} from '@/types/schema';

/** Per-type presentation of one of the card's two fixed response actions. */
export interface InteractiveMessageActionConfig {
  label: string;
  tooltip?: ReactNode;
  variant: ButtonVariant;
}

/** Fixed render order: secondary dismiss on the left, primary submit right. */
const ACTION_RENDER_ORDER = [
  MessageResponseAction.Dismiss,
  MessageResponseAction.Submit,
] as const;

interface InteractiveMessageCardProps {
  message: HouseholdChatMessage;
  /** Optional header for cards without question-level titles. */
  title?: string;
  /** Optional supporting context displayed under the title. */
  subtitle?: string | null;
  /**
   * Presentation of the two supported response actions. Renderers can hide
   * actions that do not apply to the current input. Only shown while PENDING.
   */
  actions: Record<MessageResponseAction, InteractiveMessageActionConfig>;
  /**
   * Past-tense verbs for the resolution line once the message is RESPONDED
   * (e.g. "approved"/"declined" for confirmations), keyed by the action the
   * responder took.
   */
  respondedVerbs: Record<MessageResponseAction, string>;
  /** Per-action disabled state supplied by the message-type renderer. */
  disabledActions?: Partial<Record<MessageResponseAction, boolean>>;
  /** Per-action visibility supplied by renderers with conditional footers. */
  hiddenActions?: Partial<Record<MessageResponseAction, boolean>>;
  /** Optional control aligned opposite the pending response buttons. */
  actionAccessory?: ReactNode;
  /** Optional type-specific response handler for payload-bearing messages. */
  onRespond?: (action: MessageResponseAction) => void;
  /** Body section: the type-specific reviewable content. */
  children: ReactNode;
}

/**
 * Shared shell for interactive messages — assistant messages that solicit
 * the user's response through in-card actions. Lays out the three content
 * sections (header, body, actions) as an outlined card (structure per the
 * ProminentActionBanner precedent), derives actionability from the persisted
 * message status plus the in-flight response state, wires action clicks to
 * the respond mutation, and renders the resolution line once the message
 * reaches a terminal status.
 */
export function InteractiveMessageCard({
  message,
  title,
  subtitle,
  actions,
  respondedVerbs,
  disabledActions,
  hiddenActions,
  actionAccessory,
  onRespond,
  children,
}: InteractiveMessageCardProps) {
  const { respondToMessage, respondingToMessage } = useHouseholdChatContext();

  const isSubmitting = respondingToMessage?.messageId === message.id;
  const isActionable = message.status === MessageStatus.Pending;
  const hasPendingQuestionSpacing =
    isActionable && message.type !== MessageType.ConfirmationMessage;
  const terminalStatusText = getTerminalStatusText(message, respondedVerbs);
  const handleRespond =
    onRespond ??
    ((action: MessageResponseAction) => {
      void respondToMessage({
        messageID: message.id,
        responseAction: action,
      });
    });

  return (
    <Card
      variant="outlined"
      sx={{
        px: 1,
        pb: 1,
        pt: hasPendingQuestionSpacing ? 1.5 : 1,
        borderRadius: '6px',
      }}
    >
      <Stack gap={isActionable ? 1 : 1.5}>
        {(title || subtitle) && (
          <Stack gap={1} sx={{ px: hasPendingQuestionSpacing ? 0.5 : 0 }}>
            {title && (
              <Typography variant="body-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
                {title}
              </Typography>
            )}
            {subtitle && <Typography variant="body-xs">{subtitle}</Typography>}
          </Stack>
        )}
        {children}
        {isActionable && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
            role="group"
            aria-label="Response actions"
          >
            {actionAccessory && (
              <Stack justifyContent="center" sx={{ marginRight: 'auto' }}>
                {actionAccessory}
              </Stack>
            )}
            {ACTION_RENDER_ORDER.map((action) => {
              if (hiddenActions?.[action]) {
                return null;
              }
              const actionConfig = actions[action];
              const button = (
                <Button
                  key={action}
                  variant={actionConfig.variant}
                  size="xs"
                  loading={
                    isSubmitting &&
                    action === respondingToMessage?.responseAction
                  }
                  disabled={isSubmitting || disabledActions?.[action]}
                  sx={
                    action === MessageResponseAction.Dismiss
                      ? { borderColor: COLORS.GRAY[200] }
                      : undefined
                  }
                  onClick={() => {
                    handleRespond(action);
                  }}
                >
                  {actionConfig.label}
                </Button>
              );

              return actionConfig.tooltip ? (
                <Tooltip
                  key={action}
                  enterDelay={500}
                  placement="bottom"
                  slotProps={{ tooltip: { style: { padding: 8 } } }}
                  title={actionConfig.tooltip}
                >
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </Stack>
        )}
        {terminalStatusText && (
          <Typography variant="body-xs" sx={{ color: COLORS.GRAY[400] }}>
            {terminalStatusText}
          </Typography>
        )}
      </Stack>
    </Card>
  );
}
